import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { projects } from "../../data/constants";
import ProjectCard from "../cards/ProjectCard";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50px;
  padding: 0px 16px;
  position: relative;
  z-index: 1;
  align-items: center;
  margin-bottom: 80px;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;
const Title = styled.div`
  font-size: 52px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;
const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  border: 1.5px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  font-size: 16px;
  border-radius: 12px;
  font-weight: 500;
  margin: 22px 0;
@media (max-width: 768px){
    font-size: 12px;
}
`;
const ToggleButton = styled.div`
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.primary + 40};
    color: ${({ theme }) => theme.text_secondary};
  }
  @media (max-width: 768px) {
    padding: 6px 8px;
    border-radius: 4px;
  }
  ${({ active, theme }) =>
    active &&
    `
  background:  ${theme.primary + 40};
  color: ${theme.text_secondary};
  `}
`;
const Divider = styled.div`
  width: 1.5px;
  background: ${({ theme }) => theme.primary};
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(74, 0, 224, 0.5); }
  50% { box-shadow: 0 0 20px rgba(74, 0, 224, 0.8); }
  100% { box-shadow: 0 0 5px rgba(74, 0, 224, 0.5); }
`;

const ViewMoreButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 35px auto 0 auto;
  padding: 14px 40px;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  border-radius: 0px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.primary};
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      ${({ theme }) => theme.primary + 30},
      transparent
    );
    transition: all 0.4s;
  }

  &:hover {
    color: ${({ theme }) => theme.text_primary};
    border: 2px solid ${({ theme }) => theme.text_secondary};
    animation: ${glow} 1.5s infinite;

    &:before {
      left: 100%;
    }
  }

  span {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 24px;
  }
`;

const Projects = () => {
  const [toggle, setToggle] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const handleToggle = (category) => {
    setToggle(category);
    setShowAll(false); // Reset to showing 9 items when switching tabs
  };

  // Get list based on selected category filter
  const filteredProjects = toggle === "all"
    ? projects
    : projects.filter((item) => item.category === toggle);

  // Slice visible items based on showAll state
  const visibleProjects = showAll
    ? filteredProjects
    : filteredProjects.slice(0, 9);

  return (
    <Container id="Projects">
      <Wrapper>
        <Title>My Projects</Title>
        <Desc
          style={{
            marginBottom: "40px",
          }}
        >
          Worked on diverse projects with various tech stacks 💻. Highlights below!
        </Desc>

        <ToggleButtonGroup>
          <ToggleButton
            active={toggle === "all"}
            onClick={() => handleToggle("all")}
          >
            ALL
          </ToggleButton>
          <Divider />
          <ToggleButton
            active={toggle === "web app"}
            onClick={() => handleToggle("web app")}
          >
            FULL STACK APPS
          </ToggleButton>
          <Divider />
          <ToggleButton
            active={toggle === "android app"}
            onClick={() => handleToggle("android app")}
          >
            PYTHON & AI
          </ToggleButton>
          <Divider />
          <ToggleButton
            active={toggle === "machine learning"}
            onClick={() => handleToggle("machine learning")}
          >
            REACTJS & API
          </ToggleButton>
        </ToggleButtonGroup>

        <CardContainer>
          {visibleProjects.map((project) => (
            <ProjectCard key={project.title} project={project} />
          ))}
        </CardContainer>

        {filteredProjects.length > 9 && (
          <ViewMoreButton onClick={() => setShowAll(!showAll)}>
            <span>{showAll ? "View Less" : "View More"}</span>
          </ViewMoreButton>
        )}
      </Wrapper>
    </Container>
  );
};

export default Projects;
